import React, { useState, useEffect } from 'react';
import { ClubProfile } from '../../../../utils/interfaces';

interface FairwayWoodFieldsProps {
  clubName: string;
  club: ClubProfile;
  handleProfileChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

const FairwayWoodFields: React.FC<FairwayWoodFieldsProps> = ({ clubName, club, handleProfileChange }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Check if advanced fields are filled and expand by default
  const loft = (club as any)?.loft;
  const ballFlight = (club as any)?.ball_flight;
  const attackAngle = (club as any)?.attack_angle;
  const launchAngle = (club as any)?.launch_angle;
  const spinRate = (club as any)?.spin_rate;

  useEffect(() => {
    const hasAdvancedValues = Boolean(
      loft ||
      club?.shaft ||
      club?.typical_use ||
      ballFlight ||
      attackAngle ||
      launchAngle ||
      spinRate
    );

    if (hasAdvancedValues) {
      setShowAdvanced(true);
    }
  }, [loft, club?.shaft, club?.typical_use, ballFlight, attackAngle, launchAngle, spinRate]);

  return (
    <>
      {/* Advanced section toggle */}
      <div 
        className="advanced-toggle"
        onClick={() => setShowAdvanced(!showAdvanced)}
        style={{
          cursor: 'pointer',
          padding: '12px 0',
          borderTop: '1px solid #e0e0e0',
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#666',
          fontSize: '14px',
          fontWeight: '500'
        }}
      >
        <span>Advanced Options</span>
        <span style={{ 
          transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}>
          ▼
        </span>
      </div>

      {/* Advanced fields - conditionally rendered */}
      {showAdvanced && (
        <div style={{ marginTop: '16px' }}>
          <div className="form-field">
            <label htmlFor={`${clubName}.shaft`}>Shaft</label>
            <input
              id={`${clubName}.shaft`}
              name={`${clubName}.shaft`}
              value={club?.shaft || ''}
              onChange={handleProfileChange}
              placeholder="e.g. Ventus Blue Stiff 60g"
              maxLength={40}
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.loft`}>Loft (deg)</label>
            <input
              type="number"
              step="0.5"
              id={`${clubName}.loft`}
              name={`${clubName}.loft`}
              value={(club as any)?.loft || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 15"
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.spin_rate`}>Spin Rate <br />(rpm)</label>
            <input
              type="number"
              id={`${clubName}.spin_rate`}
              name={`${clubName}.spin_rate`}
              value={(club as any)?.spin_rate || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 2400"
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.launch_angle`}>Launch <br />Angle (deg)</label>
            <input
              type="number"
              step="0.1"
              id={`${clubName}.launch_angle`}
              name={`${clubName}.launch_angle`}
              value={(club as any)?.launch_angle || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 12.5"
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.descent_angle`}>Descent <br />Angle (deg)</label>
            <input
              type="number"
              step="0.1"
              id={`${clubName}.descent_angle`}
              name={`${clubName}.descent_angle`}
              value={(club as any)?.descent_angle || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 35"
            />
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.typical_use`}>Typical Use</label>
            <select
              id={`${clubName}.typical_use`}
              name={`${clubName}.typical_use`}
              value={club?.typical_use || ''}
              onChange={handleProfileChange}
            >
              <option value="" disabled>Select...</option>
              <option value="off_tee">Off Tee</option>
              <option value="fairway">Fairway</option>
              <option value="both">Both</option>
            </select>
          </div>
          
          <div className="form-field">
            <label htmlFor={`${clubName}.ball_flight`}>Ball Flight</label>
            <select
              id={`${clubName}.ball_flight`}
              name={`${clubName}.ball_flight`}
              value={(club as any)?.ball_flight || ''}
              onChange={handleProfileChange}
            >
              <option value="" disabled>Select...</option>
              <option value="low">Low</option>
              <option value="mid">Mid</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="form-field">
            <label htmlFor={`${clubName}.attack_angle`}>Attack <br />Angle (deg)</label>
            <input
              type="number"
              step="0.1"
              id={`${clubName}.attack_angle`}
              name={`${clubName}.attack_angle`}
              value={(club as any)?.attack_angle || ''}
              onChange={handleProfileChange}
              placeholder="e.g., 2.5"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FairwayWoodFields;
